import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { ALL_PAGE_CAP } from '../../hooks/useRegistrationsTable';

const sizes = [10, 25, 50, 100, 'all'];

// First, last and two either side of the current page; a large camp has hundreds of pages.
function pageWindow(page, pageCount) {
  const pages = new Set([1, pageCount]);
  for (let p = page - 2; p <= page + 2; p += 1) if (p >= 1 && p <= pageCount) pages.add(p);
  const sorted = [...pages].sort((a, b) => a - b);
  const out = [];
  sorted.forEach((p, i) => {
    if (i && p - sorted[i - 1] > 1) out.push(`gap-${p}`);
    out.push(p);
  });
  return out;
}

export function Pagination({ page, pageCount, pageSize, pageLength, total, grouped, groupCount, onChange, onPageSizeChange }) {
  const start = total ? (page - 1) * pageLength + 1 : 0;
  const end = Math.min(page * pageLength, total);
  const arrow =
    'tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-lg tw-text-neutral-500 tw-transition-colors hover:tw-bg-sand-50 hover:tw-text-neutral-900 disabled:tw-cursor-not-allowed disabled:tw-opacity-30 disabled:hover:tw-bg-transparent';

  return (
    <nav aria-label="Pagination" className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-px-5 tw-py-3">
      <p className="tw-text-sm tw-text-neutral-500">
        {grouped && pageCount === 1 ? (
          <>
            Showing all <span className="tw-font-medium tw-tabular-nums tw-text-neutral-900">{total}</span> in {groupCount} {groupCount === 1 ? 'group' : 'groups'}
          </>
        ) : grouped ? (
          <>
            Showing <span className="tw-font-medium tw-tabular-nums tw-text-neutral-900">{start}–{end}</span> of <span className="tw-tabular-nums">{total}</span> in {groupCount}{' '}
            {groupCount === 1 ? 'group' : 'groups'}
          </>
        ) : (
          <>
            Showing <span className="tw-font-medium tw-tabular-nums tw-text-neutral-900">{start}–{end}</span> of <span className="tw-tabular-nums">{total}</span>
          </>
        )}
      </p>
      <div className="tw-flex tw-items-center tw-gap-4">
        <label className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-neutral-500">
          Rows per page
          <select
            value={String(pageSize)}
            onChange={e => onPageSizeChange(e.target.value === 'all' ? 'all' : Number(e.target.value))}
            className="tw-h-8 tw-rounded-lg tw-border tw-border-neutral-200 tw-bg-white tw-px-2 tw-text-sm tw-text-neutral-900 tw-outline-none focus:tw-border-brand-500 focus:tw-ring-2 focus:tw-ring-brand-500/20">
            {sizes.map(s => (
              <option key={String(s)} value={String(s)}>
                {s === 'all' ? `All (up to ${ALL_PAGE_CAP})` : s}
              </option>
            ))}
          </select>
        </label>
        {pageCount > 1 && (
          <div className="tw-flex tw-items-center tw-gap-1">
            <button type="button" onClick={() => onChange(page - 1)} disabled={page === 1} aria-label="Previous page" className={arrow}>
              <ChevronLeftIcon className="tw-h-4 tw-w-4" />
            </button>
            {pageWindow(page, pageCount).map(p =>
              typeof p === 'string' ? (
                <span key={p} className="tw-px-1 tw-text-sm tw-text-neutral-400" aria-hidden>
                  …
                </span>
              ) : (
                <button
                  key={p}
                  type="button"
                  onClick={() => onChange(p)}
                  aria-current={p === page ? 'page' : undefined}
                  className={`tw-h-8 tw-min-w-8 tw-rounded-lg tw-px-2 tw-text-sm tw-font-medium tw-tabular-nums tw-transition-colors tw-duration-150 ${p === page ? 'tw-bg-brand-600 tw-text-white' : 'tw-text-neutral-500 hover:tw-bg-sand-50 hover:tw-text-neutral-900'}`}>
                  {p}
                </button>
              )
            )}
            <button type="button" onClick={() => onChange(page + 1)} disabled={page === pageCount} aria-label="Next page" className={arrow}>
              <ChevronRightIcon className="tw-h-4 tw-w-4" />
            </button>
          </div>
        )}
      </div>
    </nav>
  );
}
